// Wanneer twee categorieen dezelfde categorie zijn (eigenaarsbesluit 13 okt 2026).
//
// Een categorie heeft EEN naam per begrip. 'Devops', 'DevOps' en 'DevOps / SRE' zijn
// DevOps; 'Data Engineer' is Data Engineering. Tot deze dag ontdubbelde elke schrijver
// met een eigen toLowerCase(), zodat case-varianten wegvielen maar synoniemen naast
// elkaar bleven staan in Beheer, in de filterlijst en op de records.
//
// Deze module is de enige bron (puur, zonder database):
//   canonicalCategory(name) de weergavenaam; een onbekende naam komt getrimd terug
//   categoryKey(name)       de vergelijkingssleutel: kleine letters van de canonieke naam
//   dedupeCategories(list)  ontdubbelt op categoryKey en geeft canonieke namen terug
// De ratel in lib/one-fact-one-source.test.mjs faalt zodra elders een eigen
// toLowerCase-dedupe op categorieen bijkomt.

// Canoniek: varianten. Een variant matcht hoofdletter-ongevoelig en met genormaliseerde
// witruimte; de canonieke naam zelf matcht ook in elke schrijfwijze.
const ALIASES = {
  'DevOps': ['Devops', 'DevOps / SRE'],
  'Data Engineering': ['Data Engineer'],
  'Cloud': ['Cloud Computing', 'Cloud / Platform'],
  // Besluit eigenaar 15 okt 2026: de Markt-schrijfwijze is canoniek voor BI, Bouw en
  // Business Consulting (scripts/category-pairs-2026-10-15.mjs herschrijft register en records).
  'Business Intelligence': ['BI', 'BI & Analytics'],
  'Data Analist': ['Data-Analyse', 'Data Analysis', 'Data Analytics'],
  'Data Science': ['Data Science / ML'],
  'Business Analist': ['Business Analyse'],
  // Besluit 2a (15 okt 2026): de meest gebruikte schrijfwijze is canoniek.
  'Product Owner': ['PO', 'Product Ownership'],
  'Project Management': ['Projectmanagement', 'PM'],
  'Architect': ['Architecture'],
  'Data Governance': ['Data Governance Lead'],
  'Mainframe development': ['Mainframe'],
  'Management': ['Algemeen management', 'Business Management'],
  'FE': ['["FE"', 'Front-end ontwikkeling'],
  'React': ['React.js'],
  'Vue': ['Vue.js'],
  'JavaScript': ['Javascript'],
  'Angular': ['Angular typescript'],
  'Testing': ['Testen'],
  'Functioneel testen': ['Functioneel / Manueel'],
  'Embedded': ['Embeded', 'Embedded Software', 'Embedded Systems'],
  'GWW': ['Civiele techniek', 'Civiele techniek / GWW', 'Civiel'],
  'Meet- en Regeltechniek': [],
  'Account Management': ['AccountManagement'],
  'SOC / Blue Team': ['SoC'],
  'Applicatiebeheer': ['Applicatiebeheerder'],
  'Bouw': ['Bouw & Utiliteit', 'Bouw en installatie'],
  'Installatietechniek': ['Installaties'],
  'Networking': ['Netwerken', 'Network Engineer'],
  'Azure DevOps': ['Azure DevOps Services'],
  'Controlling': ['Controllers', 'Financial control'],
  'Business Consulting': ['Business Consultant'],
};

const norm = (v) => String(v == null ? '' : v).trim().replace(/\s+/g, ' ').toLowerCase();

const LOOKUP = new Map();
for (const [canon, variants] of Object.entries(ALIASES)) {
  for (const v of [canon, ...variants]) LOOKUP.set(norm(v), canon);
}

/** De canonieke categorienaam (alleen-lezen kopie van de aliaslijst). */
export const CATEGORY_ALIASES = Object.freeze(Object.fromEntries(
  Object.entries(ALIASES).map(([k, v]) => [k, Object.freeze([...v])])));

/** Weergavenaam van een categorie; onbekend komt getrimd terug, leeg wordt ''. */
export function canonicalCategory(name) {
  const raw = String(name == null ? '' : name).trim();
  if (!raw) return '';
  return LOOKUP.get(norm(raw)) || raw;
}

/** Vergelijkingssleutel: twee categorieen zijn gelijk als hun sleutel gelijk is. */
export function categoryKey(name) {
  return canonicalCategory(name).toLowerCase();
}

/** Ontdubbelt op categoryKey, geeft canonieke namen terug, eerste wint, max lang. */
export function dedupeCategories(list, max = Infinity) {
  const out = []; const seen = new Set();
  for (const raw of Array.isArray(list) ? list : []) {
    const c = canonicalCategory(raw);
    if (!c) continue;
    const k = c.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k); out.push(c);
  }
  return out.slice(0, max);
}

/** Bevat de lijst deze categorie (op categoryKey)? */
export function hasCategory(list, name) {
  const k = categoryKey(name);
  return !!k && (Array.isArray(list) ? list : []).some((x) => categoryKey(x) === k);
}

// Namen die een skill zijn, geen categorie. Ze verdwijnen uit het register; op de
// records verhuist een latere backfill ze naar skills.
export const RETIRED_CATEGORIES = Object.freeze([
  'Python (Programming Language)', 'Microsoft SQL Server', 'Splunk Enterprise Security',
  'Debian Linux', 'Red Hat Linux', 'Kotlin', 'TypeScript', 'MUI', 'Oracle Primavera',
]);
const RETIRED_KEYS = new Set(RETIRED_CATEGORIES.map(norm));

/** Is dit een uitgefaseerde categorie (een skill)? */
export function isRetiredCategory(name) {
  return RETIRED_KEYS.has(norm(name));
}

// Registerregels onder de verkeerde markt: alleen uit taxonomy_categories weg.
export const MISFILED_CATEGORIES = Object.freeze([
  ['Controlling', 'Microcontrollers'],
  ['ERP', 'Hydrodynamics'],
  ['Finance', 'Utiliteit'],
  ['Finance', 'Security'],
  ['Architectuur', 'Pharma'],
  ['Architectuur', 'Marketing'],
].map((p) => Object.freeze(p)));

// Een markt die eigenlijk een categorie of skill was en onder een officiele markt valt.
export const MARKET_ALIASES = Object.freeze({ 'Python': 'Software Engineering' });

/**
 * Plan voor het opschonen van taxonomy_categories: per (markt, categoryKey) blijft EEN
 * regel over met de canonieke naam; uitgefaseerde en verkeerd gearchiveerde regels gaan weg.
 * Overlevende: de regel die al exact canoniek heet, anders de eerste in volgorde.
 * @param {Array<{market: string, name: string, active?: boolean}>} rows in vaste volgorde
 * @returns {{deletes: Array<{market: string, name: string}>, renames: Array<{market: string, from: string, to: string, active: boolean}>}}
 */
export function planCategoryCleanup(rows) {
  const misfiled = new Set(MISFILED_CATEGORIES.map(([m, n]) => `${m}\u0000${norm(n)}`));
  const deletes = []; const groups = new Map();
  for (const r of rows || []) {
    const market = String(r.market ?? ''); const name = String(r.name ?? '');
    if (isRetiredCategory(name) || misfiled.has(`${market}\u0000${norm(name)}`) || !canonicalCategory(name)) {
      deletes.push({ market, name }); continue;
    }
    const g = `${market}\u0000${categoryKey(name)}`;
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g).push({ market, name, active: r.active !== false });
  }
  const renames = [];
  for (const list of groups.values()) {
    const canon = canonicalCategory(list[0].name);
    const keep = list.find((r) => r.name === canon) || list[0];
    const active = list.some((r) => r.active);
    for (const r of list) if (r !== keep) deletes.push({ market: r.market, name: r.name });
    if (keep.name !== canon || keep.active !== active) renames.push({ market: keep.market, from: keep.name, to: canon, active });
  }
  return { deletes, renames };
}
